import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { ADD, EDIT, IDLE, PENDING, REVIEW } from "const"
import { CcReviewReviewItem } from "server/models"
import { hasProjectAccess, deepCopy } from "client/utils"
import {
    PRM_CC_REVIEW,
    StoreType,
    CcReviewReviewProps,
    ReviewReviewItemList,

    LoadCcReviewReviewItems,
    LoadCcReviewReviewRevisions,
    LoadCcReviewReviewItemRevisionData,
    SortCcReviewReviewItems,
    DeleteCcReviewReviewItem,

    setCcReviewReviewLoadState,
    setCcReviewReviewItemList,
    setCcReviewReviewItemIds,
    setCcReviewReviewRevisionList,
    setCcReviewReviewRevisionActive,
    setCcReviewReviewSelected,
    setCcReviewReviewSort,
} from "client/store"
import {
    Container,
    PageContent,
    PageForm,
    Button,
    Table,
    SortRows,
    DeleteRow,
} from "client/components"

import Columns from "./table/columns"
import TableBottom from "./table/bottom"
import PopupMenu from "./popupMenu"
import ConfirmDelete from "./confirmDelete"
import DisplayReviewReviewColumns from "./displayColumns"
import { OpenAnswer, OpenEdit } from "./open"

const CcReviewReviewList = ({
    reviewItemId,
    revisionId,
}: CcReviewReviewProps) => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const tableClass = useSelector((state: StoreType) => state.ccReviewReview.tableClass)
    const reloadList = useSelector((state: StoreType) => state.ccReviewReview.reloadList)

    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewReview.revisionActiveList)
    const itemList = useSelector((state: StoreType) => state.ccReviewReview.itemList)
    const itemIds = useSelector((state: StoreType) => state.ccReviewReview.itemIds)
    const sortColumn = useSelector((state: StoreType) => state.ccReviewReview.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.ccReviewReview.sortOrder)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [isOpened, setIsOpened] = useState(false)

    useEffect(() => {
        if (!reloadList) return
        if (activeFile) {
            dispatch(setCcReviewReviewLoadState(PENDING))
            LoadCcReviewReviewItems(project.id, activeFile ? activeFile.id : null)
            .then((res) => {
                dispatch(setCcReviewReviewItemList(res.itemList))
                dispatch(setCcReviewReviewSelected([]))

                const promiseArray: Promise<any>[] = []
                res.items.forEach((item) => {
                    promiseArray.push(
                        LoadCcReviewReviewRevisions(item.id).then((revisions) => {
                            const lastRevision = revisions[revisions.length - 1]
                            const activeRevision = revisionActiveList[item.id]
                            const revision = (activeRevision && revisions.find((row) => row.id === activeRevision.id)) || lastRevision

                            dispatch(setCcReviewReviewRevisionList(item.id, revisions))
                            dispatch(setCcReviewReviewRevisionActive(item.id, revision))

                            return LoadCcReviewReviewItemRevisionData(item.id, revision.id, dispatch)
                        }),
                    )
                })

                return Promise.all(promiseArray)
            }).then(() => {
                dispatch(setCcReviewReviewLoadState(IDLE))
            })
        } else {
            dispatch(setCcReviewReviewItemList({}))
        }
    }, [reloadList, activeFile])

    useEffect(() => {
        if (
            baseRoute && activeFile && activeVersion && !isOpened
            && reviewItemId !== undefined && revisionId !== undefined
        ) {
            if (window.location.href.indexOf("editItem")) {
                OpenEdit(baseRoute, REVIEW, activeFile.id, activeVersion.id, reviewItemId, revisionId, dispatch)
            } else {
                OpenAnswer(baseRoute, REVIEW, activeFile.id, activeVersion.id, reviewItemId, revisionId, dispatch)
            }

            setIsOpened(true)
        }
    }, [baseRoute, activeFile, activeVersion, isOpened])

    useEffect(() => {
        const sortedIds = SortCcReviewReviewItems(Object.values(itemList), sortColumn, sortOrder)
        dispatch(setCcReviewReviewItemIds(sortedIds))
        dispatch(setCcReviewReviewSelected([]))
    }, [itemList, sortColumn, sortOrder])

    const onSort: SortRows = (column: keyof CcReviewReviewItem, order) => {
        dispatch(setCcReviewReviewSort(column, order))
    }

    const onDelete: DeleteRow = (item: CcReviewReviewItem, callback) => {
        DeleteCcReviewReviewItem(item.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const itemListCopy: ReviewReviewItemList = itemList ? deepCopy(itemList) : {}
                delete itemListCopy[item.id]
                dispatch(setCcReviewReviewItemList(itemListCopy))
                dispatch(setCcReviewReviewItemIds(itemIds.filter((id) => id !== item.id)))
            })
        })
    }

    const { columns, displayColumns } = Columns()
    const editAccess = hasProjectAccess(PRM_CC_REVIEW, EDIT)
    const addAccess = hasProjectAccess(PRM_CC_REVIEW, ADD)

    return (
        <Container>
            <PageContent>
                {
                    addAccess && (
                        <PageForm>
                            <Button
                                variant="success"
                                disabled={!activeFile || !activeVersion}
                                onClick={() => OpenEdit(baseRoute, REVIEW, activeFile.id, activeVersion.id, 0, 0, dispatch)}
                            >
                                {lang.addReview}
                            </Button>
                        </PageForm>
                    )
                }

                <Table
                    className={tableClass}
                    columns={columns}
                    dataList={itemList}
                    dataIds={itemIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="fit"
                    showPagination={false}
                    onSort={onSort}
                    onDelete={onDelete}
                    bottomContent={editAccess && <TableBottom />}
                />

                <PopupMenu />
                <ConfirmDelete />

                <DisplayReviewReviewColumns
                    columns={columns.filter((column) => !!column.header)} // don't show management columns
                    displayColumns={displayColumns}
                />
            </PageContent>
        </Container>
    )
}
export default CcReviewReviewList
